import { Url } from '@goodt-common/utils';
import KeycloakJS from 'keycloak-js';
import { AuthAdapterError } from '@goodt-wcore/managers';
import Adapter from './Adapter';
import { isPostMessageInitRequired, promisifyAuthCredentialsRequest } from './keycloak/utils/iframe';
import { cookieExtractTokenInfo } from './keycloak/utils/cookie';
import { AdapterLoginStrategyType, AdapterLogoutStrategyType } from '../const';

/** @type {import('keycloak-js').KeycloakConfig & { postMessageInit: boolean } & { init: import('keycloak-js').KeycloakInitOptions } & import('./Keycloak').KeycloakConfigCookies & import('./Keycloak').KeycloakConfigCustomUrl |null|undefined} */
const CONFIG_DEFAULT = {
    url: '',
    realm: '',
    clientId: '',
    init: {
        checkLoginIframe: true
    },
    cookies: {
        init: false,
        tokenKey: 'KEYCLOAK_IDENTITY',
        refreshTokenKey: 'KEYCLOAK_REFRESH'
    },
    customUrl: {
        login: null,
        logout: null
    },
    postMessageInit: false
};

/** @type {import('keycloak-js').KeycloakInitOptions} */
const INIT_CONFIG_DEFAULT = {
    onLoad: 'check-sso',
    silentCheckSsoRedirectUri: Url.join(window.location.origin, window.location.pathname, 'keycloak-check-sso.html'),
    pkceMethod: 'S256',
    responseMode: 'fragment'
};

/**
 *
 */
class Keycloak extends Adapter {
    /** @type {import('keycloak-js').KeycloakInstance} */
    keycloakInstance;

    initPromise;

    /**
     * @type {import('keycloak-js').KeycloakInitOptions}
     * @private
     */
    _initConfig = INIT_CONFIG_DEFAULT;

    /**
     * @type {boolean}
     * @private
     */
    _isPostMessageInit = false;

    /**
     * Constructor
     *
     * @param {import('keycloak-js').KeycloakConfig & { init: import('keycloak-js').KeycloakInitOptions }|null|undefined} config
     */
    constructor(config) {
        /**
         * @var {import('keycloak-js').KeycloakConfig} instanceConfig
         * @var {import('keycloak-js').KeycloakInitOptions} initConfig
         */
        const { init: initConfig, postMessageInit = false, ...instanceConfig } = { ...CONFIG_DEFAULT, ...config };
        super(instanceConfig);

        this._initConfig = {
            ...INIT_CONFIG_DEFAULT,
            silentCheckSsoRedirectUri: Url.join(
                window.location.origin,
                window.location.pathname,
                'keycloak-check-sso.html'
            )
        };
        this._extendInitConfig(initConfig);
        this._isPostMessageInit = isPostMessageInitRequired(postMessageInit);

        this.keycloakInstance = KeycloakJS(instanceConfig);
        this.initPromise = null;
    }

    /**
     *
     * @private
     * @param {import('keycloak-js').KeycloakInitOptions|undefined} initConfig
     */
    _extendInitConfig(initConfig) {
        this._initConfig = {
            ...this._initConfig,
            ...initConfig
        };
    }

    /**
     * Init adapter
     *
     * @return {Promise}
     */
    init() {
        if (this.initPromise != null) {
            return this.initPromise;
        }

        /** @type {{ cookies:import('./Keycloak').KeycloakConfigCookies }} */
        const { cookies: cookiesConfig } = this.config;
        const keycloakInstanceInit = (config) => this.keycloakInstance.init({ ...this._initConfig, ...config });

        // post-message
        if (this._isPostMessageInit) {
            this.initPromise = promisifyAuthCredentialsRequest(keycloakInstanceInit);
        }
        // cookie
        else if (cookiesConfig?.init ?? false) {
            const tokenInfo = cookieExtractTokenInfo(cookiesConfig);
            this.initPromise = keycloakInstanceInit(tokenInfo);
        }
        // default
        else {
            this.initPromise = keycloakInstanceInit();
        }

        return this.initPromise;
    }

    /**
     * Returns login strat
     * @return {Promise<import('../const').IAdapterLoginStrategy>}
     */
    async getLoginStrategy() {
        const { customUrl } = this.config;

        if (customUrl?.login != null) {
            const uri = Url.create(customUrl.login);
            uri.query = { ...uri.query, redirect: window.location.href };

            return { type: AdapterLoginStrategyType.REDIRECT, options: { url: uri.toString() } };
        }

        const url = this.keycloakInstance.createLoginUrl();
        return { type: AdapterLoginStrategyType.REDIRECT, options: { url } };
    }

    /**
     * Returns logout strat
     * @return {Promise<import('../const').IAdapterLogoutStrategy>}
     */
    async getLogoutStrategy() {
        const { customUrl } = this.config;

        if (customUrl?.logout != null) {
            const url = Url.create(customUrl.logout);
            url.query = { ...url.query, redirect: window.location.href };

            return { type: AdapterLogoutStrategyType.REDIRECT, options: { url: url.toString() } };
        }

        const url = this.keycloakInstance.createLogoutUrl();
        return { type: AdapterLogoutStrategyType.REDIRECT, options: { url } };
    }

    /**
     * Login method
     *
     * @param {Record<string, any>} [credentials={}]  user credentials
     * @return {Promise<void>}
     */
    login(credentials = {}) {
        // no implemented
        // @see getLoginStrategy()
        return super.login(credentials);
    }

    /**
     * Logout method
     *
     * @return {Promise<void>}
     */
    logout() {
        // no implemented
        // @see getLogoutStrategy()
        return super.logout();
    }

    /**
     * If the token expires within minValidity seconds the token is refreshed.
     *
     * @param {number} [minValidity=60]
     * @return {Promise<boolean>}  Promise; resolve(refreshed) if token is valid/update; reject() if session expired
     */
    async updateToken(minValidity = 60) {
        try {
            const result = await this.keycloakInstance.updateToken(minValidity);
            return result;
        } catch (error) {
            if (error !== undefined) {
                throw error;
            }
            throw new AuthAdapterError('Keycloak adapter: update token failed', {
                reason: error,
                code: 0
            });
        }
    }

    /**
     * Returns user profile (depends on the adapter)
     *
     * @return {Promise.<object>}   Promise; resolve(profile); reject() on error
     */
    getUserProfile() {
        return this.keycloakInstance.loadUserProfile();
    }

    /**
     * Returns true if the token has less than minValidity seconds left before it expires (minValidity is optional, if not specified 0 is used).
     *
     * @param {number} [minValidity=5]
     * @return {boolean}
     */
    isTokenExpired(minValidity = 5) {
        return this.keycloakInstance.isTokenExpired(minValidity);
    }

    /**
     * Return auth status
     *
     * @return {boolean}
     */
    get authenticated() {
        return this.keycloakInstance.authenticated;
    }

    /**
     * Return token
     *
     * @return {string}
     */
    get token() {
        return this.keycloakInstance.token;
    }

    /**
     * Return parsed token
     *
     * @return {?object}
     */
    get tokenParsed() {
        return this.keycloakInstance.tokenParsed;
    }

    /**
     * Returns default config
     *
     * @return {Record<string, any>}
     */
    // eslint-disable-next-line class-methods-use-this
    get configDefault() {
        return { ...CONFIG_DEFAULT };
    }
}

export default Keycloak;
